import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Agent } from '@atproto/api'
import { useVerificationPreferences } from '@/hooks/useVerificationPreferences'
import { useAuth } from '@/hooks/useAuth'
import { usePlatformGuard } from '@/hooks/usePlatformGuard'
import { resolveCustomVerification } from '@/services/verificationService'
import { resolveVerificationsBatchOnServer } from '@/services/trustedVerifiersService'
import { VERIFICATION_STALE_TIME } from '@/constants/verification'
import type { CustomVerificationState } from '@/types/verification'

const DISABLED_STATE: CustomVerificationState = {
    isVerified: false,
    verifications: [],
    isLoading: false,
}

export function useCustomVerification(
    authorDid?: string,
    authorHandle?: string,
    authorDisplayName?: string
): CustomVerificationState {
    const { preferences } = useVerificationPreferences()
    const { session } = useAuth()
    const isCompatible = usePlatformGuard('atproto')
    const agent = useMemo(() => (session ? new Agent(session) : null), [session])

    const enabled = !preferences.hideAll && preferences.enabled && !!authorDid && isCompatible

    // Server-side resolution for authenticated users (batch API with single DID)
    const { data: serverData, isLoading: serverLoading } = useQuery({
        queryKey: ['customVerification', authorDid, 'server', session?.did],
        queryFn: () => resolveVerificationsBatchOnServer(agent!, [authorDid!]),
        enabled: enabled && !!agent,
        staleTime: VERIFICATION_STALE_TIME,
        retry: 0,
    })

    // Client-side fallback for unauthenticated users
    const sortedTrustedDids = useMemo(
        () =>
            preferences.trustedVerifiers
                .map(v => v.did)
                .sort()
                .join(','),
        [preferences.trustedVerifiers]
    )

    const { data: clientData, isLoading: clientLoading } = useQuery({
        queryKey: ['customVerification', authorDid, sortedTrustedDids],
        queryFn: () =>
            resolveCustomVerification(
                authorDid!,
                authorHandle ?? '',
                authorDisplayName ?? '',
                preferences.trustedVerifiers
            ),
        enabled: enabled && !agent && preferences.trustedVerifiers.length > 0,
        staleTime: VERIFICATION_STALE_TIME,
        retry: 0,
    })

    if (preferences.hideAll || !preferences.enabled) return DISABLED_STATE

    if (agent) {
        const didResult = serverData?.[authorDid!]
        const verifications = didResult?.verifiers ?? []
        return {
            isVerified: didResult?.isVerified ?? false,
            verifications,
            isLoading: serverLoading,
        }
    }

    const verifications = clientData ?? []
    return {
        isVerified: verifications.some(v => v.isValid),
        verifications,
        isLoading: clientLoading,
    }
}
